// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { toggleModal } from '../../storage/actions/modalsActions';
import Content from '../../components/clientPaymentMethods/Content';
import Loader from '../../components/dashboard/common/Loader';
// eslint-disable-next-line import/no-cycle
import Notification from '../../components/notification';
import {
  mergePaymentMethodsForList,
} from '../../utils/elavonPayments';
import DashboardIcon from '../../components/dashboard/icons/DashboardIcon';

export const ClientPaymentMethods = ({ auth, toggleModal, isReload }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const [isUpdating, setIsUpdating] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const actionInFlight = useRef(false);

  const getPaymentMethods = async () => {
    setIsLoaded(false);
    setLoadError(false);
    try {
      const res = await axios.get('/api/payment/payment-methods');
      if (res.data.success === false) throw new Error('Unable to load payment methods.');
      setPaymentMethods(mergePaymentMethodsForList(res));
      return true;
    } catch (error) {
      setLoadError(true);
      Notification('error', { message: 'Unable to refresh payment methods. Please reload the page.' });
      return false;
    } finally {
      setIsLoaded(true);
    }
  };

  const editPaymentMethod = paymentMethod => {
    toggleModal(true, 'addOrEditPaymentMethod', false, paymentMethod);
  };

  const updatePaymentMethod = async (paymentMethod, action) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setIsUpdating(true);
    try {
      const id = paymentMethod._id || paymentMethod.id;
      const response = action === 'primary'
        ? await axios.patch(`/api/payment/payment-methods/${encodeURIComponent(id)}/primary`)
        : await axios.delete('/api/payment/payment-methods', { params: { paymentMethodId: id } });
      if (response.data.success !== true) throw new Error('Payment method could not be updated.');
      if (await getPaymentMethods()) {
        Notification('success', { message: response.data.message || 'Payment method updated.' });
      }
    } catch (error) {
      Notification('error', { message: error.response?.data?.message || 'Payment method could not be updated. Please try again.' });
    } finally {
      actionInFlight.current = false;
      setIsUpdating(false);
    }
  };
  const deletePaymentMethod = paymentMethod => updatePaymentMethod(paymentMethod, 'remove');
  const makePrimary = paymentMethod => updatePaymentMethod(paymentMethod, 'primary');

  useEffect(() => {
    getPaymentMethods();
  }, [isReload]);

  return (
    <div className="subpage-wrap">
      <div className="subpage-header">
        <h1 className="subpage-title">Payment Method</h1>
        <button
          className="subpage-btn subpage-btn--mint"
          type="button"
          disabled={isUpdating}
          aria-label="add payment method"
          onClick={() => toggleModal(true, 'addOrEditPaymentMethod')}
        >
          <DashboardIcon name="payment" size={18} strokeWidth="2.5" />
          Add Payment Method
        </button>
      </div>

      {loadError && <p role="alert">Unable to load payment methods. Please reload the page.</p>}
      {isLoaded && !loadError ? (
        <Content
          paymentMethods={paymentMethods}
          editPaymentMethod={editPaymentMethod}
          deletePaymentMethod={deletePaymentMethod}
          makePrimary={makePrimary}
          auth={auth}
          isUpdating={isUpdating}
        />
      ) : !loadError && (
        <Loader />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
};

const mapStateToProps = state => ({
  auth: state.auth,
  isReload: state.applicationState.isReload,
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientPaymentMethods);
